import React, { useState, useMemo } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import AppraisalDocumentInfoModal from "./AppraisalDocumentInfoModal";
import {
  parseGoalItems,
  formatBase64Photo,
  getInitials,
  stripLeadingZeros,
  getAppraiseeIdFromStateOrStorage,
} from "./appraisalHelper";

const formatTrainingDays = (daysRaw?: string | number | null): string => {
  if (daysRaw === undefined || daysRaw === null || daysRaw === "") return "";
  const num = parseFloat(String(daysRaw));
  if (isNaN(num)) return String(daysRaw);
  return num === 1 ? "1 Day" : `${num} Days`;
};

const getRatingBadgeStyle = (rating?: string) => {
  const val = String(rating || "").toLowerCase();
  if (val.includes("outstanding")) {
    return { bg: "#E4F7E8", fg: "#1B5E20" };
  }
  if (val.includes("very good") || val.includes("verygood")) {
    return { bg: "#E3F2FD", fg: "#0D47A1" };
  }
  if (val.includes("good")) {
    return { bg: "#E0F2F1", fg: "#004D40" };
  }
  if (val.includes("average")) {
    return { bg: "#FFF8E1", fg: "#F57F17" };
  }
  if (val.includes("poor") || val.includes("below")) {
    return { bg: "#FFEBEE", fg: "#B71C1C" };
  }
  return { bg: "#F1F1F1", fg: "#333333" };
};

const AppraisalDocumentDetails = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const user = useSelector(
    (state: any) => state.employeeAuthState?.user || state.authState?.user || state.auth?.user || state.user?.userData
  );

  const rawParams = location.state?.appraisalDetail || {};
  const isEdit = location.state?.isEdit || false;

  const photoRaw =
    rawParams?.employeePhoto ||
    rawParams?.EmployeePhoto ||
    rawParams?.photo ||
    rawParams?.Photo ||
    rawParams?.raw?.EmployeePhoto ||
    rawParams?.raw?.employeePhoto ||
    "";

  const photoUri = useMemo(() => formatBase64Photo(photoRaw), [photoRaw]);
  const [imageError, setImageError] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [printLoading, setPrintLoading] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const appraisalDetail = {
    documentName: "",
    status: "",
    substatus: "",
    reportingAuthority: "",
    employeeName: "",
    validityFrom: "-",
    validityTo: "-",
    appraisalDate: "-",
    appraisalId: id || "",
    empId: "",
    employeeNumber: "",
    employeePhoto: photoRaw,
    EmployeePhoto: photoRaw,
    designation: "",
    department: "",
    payscale: "",
    dateOfBirth: "",
    dateOfAppointment: "",
    dateOfJoining: "",
    educationQualifications: "",
    qualificationsAcquiredYear: "",
    absencePeriod: "",
    earnedLeave: "",
    medicalLeave: "",
    otherLeaves: "",
    medicalCheckupDone: "",
    medicalCheckupDate: "",
    annualPropertyReturnCurrentYear: "",
    annualPropertyReturnSubmissionDate: "",
    desirableTrainings: "",
    constraintsInAchievingTarget: "",
    agreeWithEmployeeAssessment: "",
    stateOfHealth: "",
    integrity: "",
    adherenceToDmrcValuesEthics: "",
    reprimandedDispleasureMemo: "",
    exceptionalTraitTalent: "",
    trainingRecommended: "",
    templateId: "",
    participants: [] as any[],
    ratings: [] as any[],
    trainings: [] as any[],
    currentYearGoals: [] as any[],
    nextYearGoals: [] as any[],
    ...rawParams,
  };

  const currentYearGoalsList = useMemo(
    () => parseGoalItems(appraisalDetail.currentYearGoals),
    [appraisalDetail.currentYearGoals]
  );

  const nextYearGoalsList = useMemo(
    () => parseGoalItems(appraisalDetail.nextYearGoals),
    [appraisalDetail.nextYearGoals]
  );

  const participants = Array.isArray(appraisalDetail.participants)
    ? appraisalDetail.participants
    : [];

  const overallScore = useMemo(() => {
    if (!appraisalDetail.ratings?.length) return 0;
    const total = appraisalDetail.ratings.reduce((acc: number, r: any) => {
      const val = String(
        r?.ReviewingAuthorityRating || r?.ReportingAuthorityRating || ""
      ).toLowerCase();
      if (val.includes("outstanding")) return acc + 5;
      if (val.includes("very good") || val.includes("verygood")) return acc + 4;
      if (val.includes("good")) return acc + 3;
      if (val.includes("average")) return acc + 2;
      if (val.includes("poor") || val.includes("below")) return acc + 1;
      return acc;
    }, 0);
    return Number((total / appraisalDetail.ratings.length).toFixed(1));
  }, [appraisalDetail.ratings]);

  const handlePrint = async () => {
    if (printLoading) return;
    const appraisalId =
      appraisalDetail.appraisalId ||
      appraisalDetail.AppraisalID ||
      appraisalDetail.raw?.AppraisalID ||
      id ||
      "";

    const empId = stripLeadingZeros(
      appraisalDetail.empId ||
        appraisalDetail.appraiseeId ||
        appraisalDetail.AppraiseeID ||
        appraisalDetail.employeeNumber ||
        appraisalDetail.EmployeeNumber ||
        appraisalDetail.raw?.EmployeeNumber ||
        appraisalDetail.raw?.AppraiseeID ||
        getAppraiseeIdFromStateOrStorage(user) ||
        ""
    );

    if (!appraisalId || !empId) {
      toast.error("Required document details missing for print");
      return;
    }
    setPrintLoading(true);
    try {
      const response: any = await Api.post(API_URLS.APPRAISAL.PRINT, {
        appraisalId,
        empId,
      });
      const res = response?.data;
      if (res?.success || response?.status === 200) {
        const fileList = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
          ? res
          : res?.data
          ? [res.data]
          : [];

        const actualData = fileList[0] || res?.data?.data || res?.data || {};
        const contentStr =
          actualData?.Content ??
          actualData?.content ??
          actualData?.fileContent ??
          actualData?.url ??
          actualData?.attachmentUrl ??
          "";

        if (!contentStr) {
          toast.error("No document file available to print");
          return;
        }

        if (typeof contentStr === "string" && contentStr.startsWith("http")) {
          window.open(contentStr, "_blank");
        } else {
          const fileName = actualData?.FileName ?? actualData?.fileName ?? "Appraisal.pdf";
          const link = document.createElement("a");
          link.href = `data:application/pdf;base64,${contentStr}`;
          link.download = fileName;
          link.click();
        }
      } else {
        toast.error(res?.message || "Failed to generate print document");
      }
    } catch (err: any) {
      toast.error(err?.message || "Error printing document");
    } finally {
      setPrintLoading(false);
    }
  };

  const TABS = [
    appraisalDetail.documentName || "New APAR Non Executive Format",
    "Employee Self Assessment",
    "Reporting/Reviewing/Accepting Authority",
    "Overall Rating",
  ];

  const isCompleted = String(appraisalDetail.status).toLowerCase() === "completed";

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title">Appraisal Document Details</h3>
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={routes.apar.dashboard}>Employee Appraisal</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/apar/documents">Appraisal Documents</Link>
                </li>
                <li className="breadcrumb-item active">
                  {isEdit ? "Edit Document" : "Display Details"}
                </li>
              </ul>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-outline-primary btn-sm rounded-2 me-2"
                onClick={() => setShowInfoModal(true)}
              >
                <i className="ti ti-info-circle me-1"></i> Additional Info
              </button>
              <button
                className="btn btn-primary btn-sm rounded-2"
                onClick={handlePrint}
                disabled={printLoading}
              >
                {printLoading ? (
                  <>
                    <i className="ti ti-loader fa-spin me-1"></i> Generating PDF...
                  </>
                ) : (
                  <>
                    <i className="ti ti-printer me-1"></i> Print PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Hero Card */}
        <div className="card shadow-sm border-0 rounded-4 mb-4 bg-primary-transparent">
          <div className="card-body p-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div>
                <h4 className="fw-bold text-primary mb-1">
                  {appraisalDetail.employeeName
                    ? `Appraisal Document for ${appraisalDetail.employeeName}`
                    : "Appraisal Document"}
                </h4>
                <div className="d-flex align-items-center gap-3 mt-2">
                  <span
                    className={`badge rounded-pill ${
                      isCompleted
                        ? "bg-success text-white"
                        : "bg-warning text-dark"
                    }`}
                  >
                    {appraisalDetail.status || "In Process"}
                  </span>
                  <span className="text-muted fs-14">
                    <i className="ti ti-calendar me-1"></i>
                    Validity: {appraisalDetail.validityFrom} To {appraisalDetail.validityTo}
                  </span>
                </div>
              </div>

              {isEdit && (
                <span className="badge bg-danger-transparent text-danger px-3 py-2 fs-13 rounded-2">
                  <i className="ti ti-edit me-1"></i> Mode: Edit
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Administrative Data & Photo */}
        <div className="card shadow-sm border-0 rounded-4 mb-4">
          <div className="card-header bg-transparent border-0 pt-3 pb-0">
            <h5 className="card-title fw-semibold mb-0">Administrative Data</h5>
          </div>
          <div className="card-body p-4">
            <div className="row g-4 align-items-start">
              <div className="col-lg-9">
                <div className="row g-3">
                  <div className="col-md-6">
                    <span className="text-muted fs-13 d-block">DMRC APAR</span>
                    <span className="fw-semibold text-dark fs-14">
                      {appraisalDetail.documentName || "-"}
                    </span>
                  </div>

                  <div className="col-md-6">
                    <span className="text-muted fs-13 d-block">Status</span>
                    <span className="fw-bold text-dark fs-14">
                      {appraisalDetail.status || "-"}
                    </span>
                  </div>

                  <div className="col-md-6">
                    <span className="text-muted fs-13 d-block">Substatus</span>
                    <span className="fw-medium text-dark fs-14">
                      {appraisalDetail.substatus || "-"}
                    </span>
                  </div>

                  <div className="col-md-6">
                    <span className="text-muted fs-13 d-block">Reporting Authority</span>
                    <span className="fw-medium text-dark fs-14">
                      {appraisalDetail.reportingAuthority || "-"}
                    </span>
                  </div>

                  <div className="col-md-6">
                    <span className="text-muted fs-13 d-block">Employee</span>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-semibold text-dark fs-14">
                        {appraisalDetail.employeeName || "-"}
                      </span>
                      <button
                        className="btn btn-link btn-sm p-0 text-primary text-decoration-none"
                        onClick={() => setShowInfoModal(true)}
                      >
                        Info
                      </button>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <span className="text-muted fs-13 d-block">Validity Period</span>
                    <span className="fw-medium text-dark fs-14">
                      {appraisalDetail.validityFrom} To {appraisalDetail.validityTo}
                    </span>
                  </div>

                  <div className="col-md-6">
                    <span className="text-muted fs-13 d-block">Appraisal Date</span>
                    <span className="fw-medium text-dark fs-14">
                      {appraisalDetail.appraisalDate || "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Employee Photo */}
              <div className="col-lg-3 text-center">
                <div
                  className="card p-2 border border-2 shadow-xs rounded-4 d-inline-block"
                  style={{ width: "140px", height: "170px" }}
                >
                  {photoUri && !imageError ? (
                    <img
                      src={photoUri}
                      alt="Employee"
                      className="rounded-3 w-100 h-100 object-fit-cover"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className="w-100 h-100 bg-light rounded-3 d-flex flex-column align-items-center justify-content-center">
                      <i className="ti ti-user fs-40 text-muted"></i>
                      <span className="fw-bold text-muted mt-2">
                        {getInitials(appraisalDetail.employeeName)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <button
                    className="btn btn-sm btn-outline-secondary rounded-pill fs-12"
                    onClick={() => setShowInfoModal(true)}
                  >
                    View Appraisee Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Further Participants */}
        {participants.length > 0 && (
          <div className="card shadow-sm border-0 rounded-4 mb-4">
            <div className="card-header bg-transparent border-0 pt-3 pb-0">
              <h5 className="card-title fw-semibold mb-0">Further Participants</h5>
            </div>
            <div className="card-body p-3">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "60px" }}>Avatar</th>
                      <th>Further Participants</th>
                      <th>Appraisal Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((p: any, idx: number) => (
                      <tr key={idx}>
                        <td>
                          <div className="avatar avatar-sm bg-primary-transparent text-primary rounded-circle fw-bold">
                            {getInitials(p.name)}
                          </div>
                        </td>
                        <td className="fw-semibold text-dark">{p.name}</td>
                        <td>
                          <span className="badge bg-secondary-transparent text-secondary">
                            {p.role}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Tabs Navigation */}
        <div className="card shadow-sm border-0 rounded-4">
          <div className="card-header bg-transparent border-bottom-0 pb-0 pt-3">
            <ul className="nav nav-tabs nav-tabs-bottom border-bottom">
              {TABS.map((tab, idx) => (
                <li key={idx} className="nav-item">
                  <button
                    className={`nav-link fw-semibold ${activeTab === idx ? "active text-primary" : "text-muted"}`}
                    onClick={() => setActiveTab(idx)}
                  >
                    {tab}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-body p-4">
            {/* Tab 0: Document Details & Trainings */}
            {activeTab === 0 && (
              <div className="row g-4">
                <div className="col-12">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2">Document Details</h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <span className="text-muted fs-13 d-block">Template ID</span>
                      <span className="fw-medium text-dark">{appraisalDetail.templateId || "-"}</span>
                    </div>
                    <div className="col-md-4">
                      <span className="text-muted fs-13 d-block">Appraisal ID</span>
                      <span className="fw-medium text-dark">{appraisalDetail.appraisalId || "-"}</span>
                    </div>
                    {appraisalDetail.designation && (
                      <div className="col-md-4">
                        <span className="text-muted fs-13 d-block">Designation</span>
                        <span className="fw-medium text-dark">{appraisalDetail.designation}</span>
                      </div>
                    )}
                    {appraisalDetail.department && (
                      <div className="col-md-4">
                        <span className="text-muted fs-13 d-block">Department</span>
                        <span className="fw-medium text-dark">{appraisalDetail.department}</span>
                      </div>
                    )}
                    {appraisalDetail.payscale && (
                      <div className="col-md-4">
                        <span className="text-muted fs-13 d-block">Payscale</span>
                        <span className="fw-medium text-dark">{appraisalDetail.payscale}</span>
                      </div>
                    )}
                    {appraisalDetail.absencePeriod && (
                      <div className="col-md-4">
                        <span className="text-muted fs-13 d-block">Period of Absence</span>
                        <span className="fw-medium text-dark">{appraisalDetail.absencePeriod}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 mt-4">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2">
                    Trainings Attended ({appraisalDetail.trainings?.length || 0})
                  </h6>
                  {appraisalDetail.trainings?.length > 0 ? (
                    <div className="row g-3">
                      {appraisalDetail.trainings.map((tr: any, idx: number) => (
                        <div key={idx} className="col-md-6">
                          <div className="card border rounded-3 p-3 h-100 bg-light">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <i className="ti ti-school text-primary fs-18"></i>
                              <span className="fw-bold text-dark">
                                {tr?.TrainingAttendedNotes || `Training ${idx + 1}`}
                              </span>
                            </div>
                            <div className="row g-2 fs-13 text-muted">
                              <div className="col-6">Start Date: {tr?.TrainingStartDate || "-"}</div>
                              <div className="col-6">Location: {tr?.Location || "-"}</div>
                              {tr?.NoOfDays && (
                                <div className="col-12">
                                  Duration: {formatTrainingDays(tr.NoOfDays)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted fs-14 mb-0">No training records available.</p>
                  )}
                </div>
              </div>
            )}

            {/* Tab 1: Employee Self Assessment */}
            {activeTab === 1 && (
              <div className="row g-4">
                <div className="col-12">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2">
                    Current Year Goals / Targets ({currentYearGoalsList.length})
                  </h6>
                  {currentYearGoalsList.length > 0 ? (
                    <ol className="list-group list-group-numbered mb-0">
                      {currentYearGoalsList.map((goalText: string, idx: number) => (
                        <li key={idx} className="list-group-item border-0 bg-light mb-2 rounded-2 fs-14">
                          {goalText}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-muted fs-14 mb-0">No current year goals recorded.</p>
                  )}
                </div>

                <div className="col-12 mt-4">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2">
                    Next Year Goals / Targets ({nextYearGoalsList.length})
                  </h6>
                  {nextYearGoalsList.length > 0 ? (
                    <ol className="list-group list-group-numbered mb-0">
                      {nextYearGoalsList.map((goalText: string, idx: number) => (
                        <li key={idx} className="list-group-item border-0 bg-light mb-2 rounded-2 fs-14">
                          {goalText}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-muted fs-14 mb-0">No next year goals recorded.</p>
                  )}
                </div>

                <div className="col-12 mt-4">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2">
                    Development & Constraints
                  </h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <span className="text-muted fs-13 d-block">Desirable Trainings</span>
                      <span className="fw-medium text-dark fs-14">
                        {appraisalDetail.desirableTrainings || "NIL"}
                      </span>
                    </div>
                    <div className="col-md-6">
                      <span className="text-muted fs-13 d-block">Constraints in Achieving Target</span>
                      <span className="fw-medium text-dark fs-14">
                        {appraisalDetail.constraintsInAchievingTarget || "NIL"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 2: Reporting/Reviewing/Accepting Authority */}
            {activeTab === 2 && (
              <div className="row g-4">
                <div className="col-12">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2">Authority Assessment</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <span className="text-muted fs-13 d-block">Agree with Employee Assessment</span>
                      <span className="fw-bold text-dark fs-14">
                        {appraisalDetail.agreeWithEmployeeAssessment || "-"}
                      </span>
                    </div>
                    <div className="col-md-6">
                      <span className="text-muted fs-13 d-block">State of Health</span>
                      <span className="fw-medium text-dark fs-14">
                        {appraisalDetail.stateOfHealth || "-"}
                      </span>
                    </div>
                    <div className="col-md-6">
                      <span className="text-muted fs-13 d-block">Integrity</span>
                      <span className="fw-bold text-dark fs-14">
                        {appraisalDetail.integrity || "-"}
                      </span>
                    </div>
                    <div className="col-md-6">
                      <span className="text-muted fs-13 d-block">Adherence to DMRC Values & Ethics</span>
                      <span className="fw-medium text-dark fs-14">
                        {appraisalDetail.adherenceToDmrcValuesEthics || "-"}
                      </span>
                    </div>
                    <div className="col-md-6">
                      <span className="text-muted fs-13 d-block">Reprimand / Displeasure Memo</span>
                      <span className="fw-medium text-dark fs-14">
                        {appraisalDetail.reprimandedDispleasureMemo || "NIL"}
                      </span>
                    </div>
                    <div className="col-md-6">
                      <span className="text-muted fs-13 d-block">Exceptional Trait / Talent</span>
                      <span className="fw-medium text-dark fs-14">
                        {appraisalDetail.exceptionalTraitTalent || "NIL"}
                      </span>
                    </div>
                    <div className="col-md-6">
                      <span className="text-muted fs-13 d-block">Training Recommended</span>
                      <span className="fw-medium text-dark fs-14">
                        {appraisalDetail.trainingRecommended || "NIL"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="col-12 mt-4">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2">
                    Authority Performance Ratings ({appraisalDetail.ratings?.length || 0})
                  </h6>
                  {appraisalDetail.ratings?.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Rating Category</th>
                            <th>Reporting Rating</th>
                            <th>Reviewing Rating</th>
                            <th>Accepting Rating</th>
                          </tr>
                        </thead>
                        <tbody>
                          {appraisalDetail.ratings.map((r: any, idx: number) => {
                            const repStyle = getRatingBadgeStyle(r?.ReportingAuthorityRating);
                            const revStyle = getRatingBadgeStyle(r?.ReviewingAuthorityRating);
                            const accStyle = getRatingBadgeStyle(r?.AcceptingAuthorityRating);
                            return (
                              <tr key={idx}>
                                <td>{idx + 1}</td>
                                <td className="fw-semibold">{r?.RatingCategory || "Rating"}</td>
                                <td>
                                  {r?.ReportingAuthorityRating ? (
                                    <span
                                      className="badge rounded-2 px-2 py-1"
                                      style={{ backgroundColor: repStyle.bg, color: repStyle.fg }}
                                    >
                                      {r.ReportingAuthorityRating}
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td>
                                  {r?.ReviewingAuthorityRating ? (
                                    <span
                                      className="badge rounded-2 px-2 py-1"
                                      style={{ backgroundColor: revStyle.bg, color: revStyle.fg }}
                                    >
                                      {r.ReviewingAuthorityRating}
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td>
                                  {r?.AcceptingAuthorityRating ? (
                                    <span
                                      className="badge rounded-2 px-2 py-1"
                                      style={{ backgroundColor: accStyle.bg, color: accStyle.fg }}
                                    >
                                      {r.AcceptingAuthorityRating}
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted fs-14 mb-0">No rating evaluations recorded.</p>
                  )}
                </div>
              </div>
            )}

            {/* Tab 3: Overall Rating */}
            {activeTab === 3 && (
              <div className="row g-4 align-items-center">
                <div className="col-md-4 text-center border-end">
                  <div className="d-inline-flex flex-column align-items-center justify-content-center p-4 rounded-circle bg-primary-transparent border border-primary border-3" style={{ width: "140px", height: "140px" }}>
                    <h2 className="fw-bold text-primary mb-0">{overallScore}</h2>
                    <span className="text-muted fs-12">Out of 5.0</span>
                  </div>
                  <h6 className="fw-bold text-dark mt-3 mb-1">Average Score</h6>
                  <p className="text-muted fs-13 mb-0">
                    Rated by: {appraisalDetail.reportingAuthority || "-"}
                  </p>
                </div>

                <div className="col-md-8">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2">Ratings Breakup</h6>
                  {appraisalDetail.ratings?.length > 0 ? (
                    <div className="list-group list-group-flush">
                      {appraisalDetail.ratings.map((r: any, idx: number) => {
                        const finalRating =
                          r?.ReviewingAuthorityRating ||
                          r?.ReportingAuthorityRating ||
                          r?.AcceptingAuthorityRating ||
                          "-";
                        const badgeStyle = getRatingBadgeStyle(finalRating);
                        return (
                          <div key={idx} className="list-group-item d-flex justify-content-between align-items-center px-0 py-2">
                            <span className="fw-medium text-dark fs-14">{r?.RatingCategory}</span>
                            <span
                              className="badge rounded-pill px-3 py-2 fs-12"
                              style={{ backgroundColor: badgeStyle.bg, color: badgeStyle.fg }}
                            >
                              {finalRating}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted fs-14 mb-0">No detailed ratings found.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Modal */}
      <AppraisalDocumentInfoModal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        appraisalDetail={appraisalDetail}
      />
    </div>
  );
};

export default AppraisalDocumentDetails;
